import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit, Trash2, Plus, X, Save, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { image: '', text_en: '', text_zh: '', sort_order: 0 };

const SfhHomesManager = ({ isZh = false, token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/sfh-homes`);
      setItems(r.data?.items || []);
    } catch {
      toast.error(isZh ? '加载失败' : 'Load failed');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => setEditing({ ...empty, sort_order: items.length + 1 });
  const startEdit = (i) => setEditing({ ...i });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.image && !editing.text_en && !editing.text_zh) {
      toast.error(isZh ? '请至少填写图片或文字' : 'Please provide an image or text');
      return;
    }
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/sfh-homes/${editing.id}`, editing, { headers });
        toast.success(isZh ? '已保存' : 'Saved');
      } else {
        await axios.post(`${API}/admin/sfh-homes`, editing, { headers });
        toast.success(isZh ? '已创建' : 'Created');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isZh ? '保存失败' : 'Save failed'));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(isZh ? '确认删除？' : 'Delete this entry?')) return;
    try {
      await axios.delete(`${API}/admin/sfh-homes/${id}`, { headers });
      toast.success(isZh ? '已删除' : 'Deleted');
      load();
    } catch { toast.error(isZh ? '删除失败' : 'Delete failed'); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/admin/upload`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.url || r.data?.path || '';
      if (url) {
        setEditing({ ...editing, image: url });
        toast.success(isZh ? '上传成功' : 'Uploaded');
      }
    } catch { toast.error(isZh ? '上传失败' : 'Upload failed'); }
    finally { setUploading(false); }
  };

  // ---------- LIST VIEW ----------
  if (!editing) {
    return (
      <div data-testid="sfh-homes-manager">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">{isZh ? '独栋住宅画廊' : 'Single-Family Homes Gallery'}</h2>
          <Button onClick={startNew} className="bg-red-600 hover:bg-red-700 text-white" data-testid="sfh-new-btn">
            <Plus className="w-4 h-4 mr-1" />{isZh ? '新建' : 'New'}
          </Button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />{isZh ? '加载中...' : 'Loading...'}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-gray-500">{isZh ? '暂无数据，点击新建' : 'No entries yet. Click "New" to add.'}</div>
        ) : (
          <div className="grid gap-3">
            {items.map(it => (
              <Card key={it.id} className="hover:shadow-md transition" data-testid={`sfh-row-${it.id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  {it.image ? (
                    <img src={it.image} alt="" className="w-24 h-20 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-24 h-20 rounded bg-red-50 flex items-center justify-center text-red-400 flex-shrink-0"><ImageIcon className="w-6 h-6" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-base line-clamp-2">{it.text_en || it.text_zh || '(no text)'}</div>
                    {it.text_zh && it.text_en && <div className="text-xs text-gray-500 line-clamp-1 mt-1">{it.text_zh}</div>}
                    <div className="text-xs text-gray-400 mt-1">{isZh ? '排序' : 'Sort'}: {it.sort_order ?? 0}</div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => startEdit(it)} data-testid={`sfh-edit-${it.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(it.id)} className="text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- EDIT VIEW ----------
  return (
    <div data-testid="sfh-editor">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">{editing.id ? (isZh ? '编辑条目' : 'Edit Entry') : (isZh ? '新建条目' : 'New Entry')}</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={cancel}><X className="w-4 h-4 mr-1" />{isZh ? '取消' : 'Cancel'}</Button>
          <Button onClick={save} className="bg-red-600 hover:bg-red-700 text-white" data-testid="sfh-save-btn"><Save className="w-4 h-4 mr-1" />{isZh ? '保存' : 'Save'}</Button>
        </div>
      </div>
      <Card><CardContent className="p-6 space-y-4">
        <div>
          <label className="text-sm font-medium block mb-1">{isZh ? '图片' : 'Image'}</label>
          {editing.image && <img src={editing.image} alt="" className="w-64 h-44 object-cover rounded mb-2 border" />}
          <div className="flex gap-2">
            <Input value={editing.image} onChange={e => setEditing({ ...editing, image: e.target.value })} placeholder="https://..." data-testid="sfh-image-input" />
            <input type="file" accept="image/*" ref={fileRef} onChange={uploadImage} className="hidden" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
              <span className="ml-1">{isZh ? '上传' : 'Upload'}</span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium block mb-1">{isZh ? '英文文字' : 'Text (English)'}</label>
            <textarea
              value={editing.text_en}
              onChange={e => setEditing({ ...editing, text_en: e.target.value })}
              rows={4}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              placeholder="Modern 2,400 sqft single-family home in Pasadena..."
              data-testid="sfh-text-en"
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">{isZh ? '中文文字' : 'Text (Chinese)'}</label>
            <textarea
              value={editing.text_zh}
              onChange={e => setEditing({ ...editing, text_zh: e.target.value })}
              rows={4}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              placeholder="位于帕萨迪纳的现代独栋住宅..."
              data-testid="sfh-text-zh"
            />
          </div>
        </div>

        <div className="max-w-xs">
          <label className="text-sm font-medium block mb-1">{isZh ? '排序' : 'Sort Order'}</label>
          <Input
            type="number"
            value={editing.sort_order}
            onChange={e => setEditing({ ...editing, sort_order: parseInt(e.target.value, 10) || 0 })}
            data-testid="sfh-sort"
          />
          <p className="text-xs text-gray-500 mt-1">{isZh ? '数值越小越靠前' : 'Lower numbers appear first.'}</p>
        </div>
      </CardContent></Card>
    </div>
  );
};

export default SfhHomesManager;
